import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { dashAPI } from "../api";
import { toast } from "react-toastify";

const JobsRoute = "careerapi/api/v1/jobs";
const CandidatesRoute = "careerapi/api/v1/candidates";
const ApplicationsRoute = "careerapi/api/v1/applications";

// ==========================================
// --- ASYNC THUNKS: JOBS & CANDIDATES ---
// ==========================================

export const fetchJobs = createAsyncThunk(
  "career/fetchJobs",
  async (_, { rejectWithValue }) => {
    try {
      const response = await dashAPI.get(JobsRoute);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchJob = createAsyncThunk(
  "career/fetchJob",
  async (id, { rejectWithValue }) => {
    try {
      const response = await dashAPI.get(`${JobsRoute}/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const createJob = createAsyncThunk(
  "career/createJob",
  async (values, { rejectWithValue }) => {
    try {
      const response = await dashAPI.post(JobsRoute, values);
      toast.success("Job created successfully!");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const updateJob = createAsyncThunk(
  "career/updateJob",
  async ({ id, values }, { rejectWithValue }) => {
    try {
      const response = await dashAPI.patch(`${JobsRoute}/${id}`, values);
      toast.success("Job updated successfully!");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const deleteJob = createAsyncThunk(
  "career/deleteJob",
  async (id, { rejectWithValue }) => {
    try {
      await dashAPI.delete(`${JobsRoute}/${id}`);
      toast.success("Job deleted successfully!");
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchCandidates = createAsyncThunk(
  "career/fetchCandidates",
  async (_, { rejectWithValue }) => {
    try {
      const response = await dashAPI.get(CandidatesRoute);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const createCandidate = createAsyncThunk(
  "career/createCandidate",
  async (values, { rejectWithValue }) => {
    try {
      const response = await dashAPI.post(CandidatesRoute, values);
      toast.success("Candidate created successfully!");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const deleteCandidate = createAsyncThunk(
  "career/deleteCandidate",
  async (id, { rejectWithValue }) => {
    try {
      await dashAPI.delete(`${CandidatesRoute}/${id}`);
      toast.success("Candidate deleted successfully!");
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

// ==========================================
// --- ASYNC THUNKS: APPLICATIONS ---
// ==========================================

export const fetchApplications = createAsyncThunk(
  "career/fetchApplications",
  async (_, { rejectWithValue }) => {
    try {
      const response = await dashAPI.get(ApplicationsRoute);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const createApplication = createAsyncThunk(
  "career/createApplication",
  async (values, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      // 1. Append the Job ID
      if (values.jobId) formData.append("jobId", values.jobId);

      // 2. Append the Candidate properties dynamically
      if (values.candidate) {
        if (values.candidate.firstName)
          formData.append("candidate.firstName", values.candidate.firstName);
        if (values.candidate.lastName)
          formData.append("candidate.lastName", values.candidate.lastName);
        if (values.candidate.email)
          formData.append("candidate.email", values.candidate.email);
        if (values.candidate.phone)
          formData.append("candidate.phone", values.candidate.phone);
      }

      // 3. Append the nested media batch items
      if (values.files && values.files.length > 0) {
        values.files.forEach((file, index) => {
          formData.append(`media.items[${index}].file`, file);
          formData.append(`media.items[${index}].meta`, JSON.stringify({}));
        });
      }

      const response = await dashAPI.post(ApplicationsRoute, formData);
      toast.success("Application submitted successfully!");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

// WARNING: The backend swagger removed PATCH /api/v1/applications/{id}.
// This route will return a 404/405 error until the backend team restores it.
export const updateApplication = createAsyncThunk(
  "career/updateApplication",
  async ({ id, values }, { rejectWithValue }) => {
    try {
      const response = await dashAPI.patch(
        `${ApplicationsRoute}/${id}`,
        values,
      );
      toast.success("Application updated successfully!");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const deleteApplication = createAsyncThunk(
  "career/deleteApplication",
  async (id, { rejectWithValue }) => {
    try {
      // FIX: The backend now expects ?uuid= query parameter instead of a path variable
      await dashAPI.delete(`${ApplicationsRoute}?uuid=${id}`);
      toast.success("Application deleted successfully!");
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

// ==========================================
// --- SLICE & REDUCER ---
// ==========================================

const careerSlice = createSlice({
  name: "career",
  initialState: {
    loading: false,
    error: "",
    jobs: [],
    job: {},
    candidates: [],
    candidate: {},
    applications: [],
    application: {},
  },
  reducers: {
    clearCurrentJob: (state) => {
      state.job = {};
    },
    clearCurrentCandidate: (state) => {
      state.candidate = {};
    },
    clearCurrentApplication: (state) => {
      state.application = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // Jobs
      .addCase(fetchJobs.fulfilled, (state, action) => {
        state.loading = false;
        state.jobs = action.payload;
      })
      .addCase(fetchJob.fulfilled, (state, action) => {
        state.loading = false;
        state.job = action.payload;
      })
      .addCase(createJob.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) state.jobs.unshift(action.payload);
      })
      .addCase(updateJob.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.jobs.findIndex((j) => j.id === action.payload.id);
        if (index !== -1) state.jobs[index] = action.payload;
        if (state.job.id === action.payload.id) state.job = action.payload;
      })
      .addCase(deleteJob.fulfilled, (state, action) => {
        state.loading = false;
        state.jobs = state.jobs.filter((j) => j.id !== action.payload);
      })

      // Candidates
      .addCase(fetchCandidates.fulfilled, (state, action) => {
        state.loading = false;
        state.candidates = action.payload;
      })
      .addCase(createCandidate.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) state.candidates.unshift(action.payload);
      })
      .addCase(deleteCandidate.fulfilled, (state, action) => {
        state.loading = false;
        state.candidates = state.candidates.filter(
          (c) => c.id !== action.payload,
        );
      })

      // Applications
      .addCase(fetchApplications.fulfilled, (state, action) => {
        state.loading = false;
        state.applications = action.payload;
      })
      .addCase(createApplication.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) state.applications.unshift(action.payload);
      })
      .addCase(updateApplication.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.applications.findIndex(
          (a) => a.id === action.payload.id,
        );
        if (index !== -1) state.applications[index] = action.payload;
        if (state.application.id === action.payload.id)
          state.application = action.payload;
      })
      .addCase(deleteApplication.fulfilled, (state, action) => {
        state.loading = false;
        state.applications = state.applications.filter(
          (a) => a.id !== action.payload,
        );
      })

      // Global Matchers
      .addMatcher(
        (action) =>
          action.type.startsWith("career/") && action.type.endsWith("/pending"),
        (state) => {
          state.loading = true;
          state.error = "";
        },
      )
      .addMatcher(
        (action) =>
          action.type.startsWith("career/") &&
          action.type.endsWith("/rejected"),
        (state, action) => {
          state.loading = false;
          state.error = action.payload;
          toast.error(
            action.payload || "An error occurred with the Career API",
          );
        },
      );
  },
});

export const {
  clearCurrentJob,
  clearCurrentCandidate,
  clearCurrentApplication,
} = careerSlice.actions;
export const CareerReducer = careerSlice.reducer;
